/**
 * Visning av kundprojekt på kundkortet, i kontaktrutorna, på leadsidan, på
 * kanbankortet och som rad på Pipeline-sidan. Logiken ligger i
 * kundprojekt.js, Firestore i kundprojektData.js.
 *
 * Kundprojekt är kundens projekt som säljunderlag – inte Tikabs uppdrag
 * eller referensprojekt.
 */
import React, { useState } from 'react';
import {
  Box, Paper, Typography, Chip, Tooltip, Button, IconButton, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, List, ListItemButton, ListItemText, ListSubheader, Alert,
  ToggleButtonGroup, ToggleButton, CircularProgress, MenuItem,
} from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import ConstructionIcon from '@mui/icons-material/Construction';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import EditIcon from '@mui/icons-material/Edit';
import {
  etikett, partFor, kontaktensRoll, medLead, utanLead, manadText, arSakerhetsskyddat, arIsoleradMiljo,
  KP_ROLLER, KP_BYGGSKEDEN, KP_ENTREPRENADFORMER, KP_STATUSAR, JA_NEJ_OKANT, SAKERHET_VARNING,
} from './kundprojekt';
import { sparaKundprojektAndringar, laddaAllaKundprojekt, sparaKundSakerhet } from './kundprojektData';
import KundprojektRedigering, { useSparatKvitto, KONFLIKT_TEXT } from './KundprojektRedigering';

const ORANGE = '#E65100';

/** Ikoner för säkerhetsskydd och isolerad miljö. */
export function SakerhetsIkoner({ kp, storlek = 15 }) {
  return (
    <>
      {arSakerhetsskyddat(kp) && (
        <Tooltip title="Säkerhetsskyddsklassat kundprojekt">
          <ShieldOutlinedIcon sx={{ fontSize: storlek, color: ORANGE }} />
        </Tooltip>
      )}
      {arIsoleradMiljo(kp) && (
        <Tooltip title="Isolerad/airgappad miljö">
          <CloudOffOutlinedIcon sx={{ fontSize: storlek, color: '#6A1B9A' }} />
        </Tooltip>
      )}
    </>
  );
}

const sorteraKp = kps => [...kps].sort((a, b) =>
  ((a.status || 'aktivt') !== 'aktivt') - ((b.status || 'aktivt') !== 'aktivt')
  || String(a.namn).localeCompare(String(b.namn), 'sv'));

// ── Kundkortet: säkerhetsflaggor ────────────────────────────────────────────

const FLAGGA = { ja: { bg: '#FFF3E0', color: ORANGE }, nej: { bg: '#f5f5f5', color: '#757575' }, okant: { bg: '#f5f5f5', color: '#9e9e9e' } };

function Flagga({ ikon, text, varde }) {
  const f = FLAGGA[varde || 'okant'];
  return (
    <Chip size="small" icon={ikon} label={`${text}: ${etikett(JA_NEJ_OKANT, varde || 'okant')}`}
      sx={{ bgcolor: f.bg, color: f.color, fontWeight: varde === 'ja' ? 700 : 500, '& .MuiChip-icon': { color: f.color } }} />
  );
}

/** Överst på kundkortet. Flaggorna sätts automatiskt från kundprojekt och kan ändras här. */
export function KundSakerhetFlaggor({ kund, onSparad }) {
  const [redigera, setRedigera] = useState(null);
  const [sparar, setSparar] = useState(false);

  async function spara() {
    setSparar(true);
    try {
      const falt = await sparaKundSakerhet(kund.id, redigera);
      onSparad?.(falt);
      setRedigera(null);
    } finally {
      setSparar(false);
    }
  }

  const val = (falt, label) => (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{label}</Typography>
      <ToggleButtonGroup size="small" exclusive value={redigera[falt] || 'okant'}
        onChange={(_, x) => x && setRedigera(r => ({ ...r, [falt]: x }))}
        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.75, py: 0.25,
          '&.Mui-selected': { bgcolor: '#1a1a1a', color: 'white', '&:hover': { bgcolor: '#333' } } } }}>
        {JA_NEJ_OKANT.map(x => <ToggleButton key={x.key} value={x.key}>{x.label}</ToggleButton>)}
      </ToggleButtonGroup>
    </Box>
  );

  return (
    <Paper sx={{ px: 2.5, py: 1.25, mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Flagga ikon={<ShieldOutlinedIcon />} text="Erfarenhet av säkerhetsskydd" varde={kund.erfarenhetSakerhetsskydd} />
      <Flagga ikon={<CloudOffOutlinedIcon />} text="Isolerad miljö" varde={kund.harIsoleradMiljo} />
      {kund.sakerhetsAnteckning && (
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', mx: 0.5 }}>
          {kund.sakerhetsAnteckning}
        </Typography>
      )}
      <Tooltip title="Ändra säkerhetsflaggor">
        <IconButton size="small" sx={{ ml: 'auto' }} aria-label="Ändra säkerhetsflaggor"
          onClick={() => setRedigera({
            erfarenhetSakerhetsskydd: kund.erfarenhetSakerhetsskydd || 'okant',
            harIsoleradMiljo: kund.harIsoleradMiljo || 'okant',
            sakerhetsAnteckning: kund.sakerhetsAnteckning || '',
          })}>
          <EditIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      <Dialog open={!!redigera} onClose={() => setRedigera(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Säkerhetsflaggor</DialogTitle>
        {redigera && (
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
            {val('erfarenhetSakerhetsskydd', 'Erfarenhet av säkerhetsskydd')}
            {val('harIsoleradMiljo', 'Har isolerad miljö')}
            <TextField size="small" fullWidth label="Anteckning" value={redigera.sakerhetsAnteckning}
              onChange={e => setRedigera(r => ({ ...r, sakerhetsAnteckning: e.target.value }))}
              helperText={SAKERHET_VARNING} inputProps={{ maxLength: 200 }} />
            <Typography variant="caption" color="text.secondary">
              Flaggorna sätts till Ja automatiskt när kunden blir part i ett säkerhetsskyddat kundprojekt
              eller ett med isolerad miljö. De sätts aldrig tillbaka automatiskt.
            </Typography>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setRedigera(null)} sx={{ color: 'text.secondary' }}>Avbryt</Button>
          <Button variant="contained" onClick={spara} disabled={sparar}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            {sparar ? 'Sparar…' : 'Spara'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// ── Kundkortet: lista ───────────────────────────────────────────────────────

/** Rad med kundens roll, byggskede, skedestart och entreprenadform. */
function Detaljer({ kp, kundId }) {
  const part = partFor(kp, kundId);
  const delar = [
    etikett(KP_ROLLER, part?.roll),
    etikett(KP_BYGGSKEDEN, kp.byggskede),
    kp.skedeStart && `start ${manadText(kp.skedeStart)}`,
    kp.entreprenadform && kp.entreprenadform !== 'okant' && etikett(KP_ENTREPRENADFORMER, kp.entreprenadform),
  ].filter(Boolean);
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {delar.join(' · ') || '–'}
    </Typography>
  );
}

/** Namn (länk till kundprojektets sida när navigate finns), ikoner och status. */
function Rubrik({ kp, navigate }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
      <Typography variant="body2" fontWeight={700} noWrap
        {...(navigate ? {
          role: 'link', tabIndex: 0,
          onClick: e => { e.stopPropagation(); navigate(`/crm/kundprojekt/${kp.id}`); },
          onKeyDown: e => { if (e.key === 'Enter') navigate(`/crm/kundprojekt/${kp.id}`); },
          sx: { cursor: 'pointer', '&:hover': { color: '#FF6B00', textDecoration: 'underline' } },
        } : {})}>
        {kp.namn}
      </Typography>
      <SakerhetsIkoner kp={kp} />
      {(kp.status || 'aktivt') !== 'aktivt' && (
        <Chip size="small" label={etikett(KP_STATUSAR, kp.status)} sx={{ height: 17, fontSize: '0.6rem' }} />
      )}
    </Box>
  );
}

/**
 * Kundens kundprojekt med kopplade leads.
 * leads: leadobjekt för kundens leadkopplingar (titel), navigate för klick.
 */
export function KundprojektLista({ kps, kundId, leads = [], navigate, laddar, uid, onAndrat }) {
  const [redigera, setRedigera] = useState(null);
  const [kvitto, visaKvitto] = useSparatKvitto();
  if (laddar) return <CircularProgress size={18} sx={{ mt: 1 }} />;
  if (!kps.length) {
    return <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Inga kundprojekt registrerade.</Typography>;
  }
  const leadMap = new Map(leads.map(l => [l.id, l]));
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
      {sorteraKp(kps).map(kp => {
        const leadIds = partFor(kp, kundId)?.leadIds || [];
        return (
          <Box key={kp.id} sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
            borderLeft: arSakerhetsskyddat(kp) ? `3px solid ${ORANGE}` : undefined,
            opacity: (kp.status || 'aktivt') === 'aktivt' ? 1 : 0.7 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Rubrik kp={kp} navigate={navigate} />
                <Detaljer kp={kp} kundId={kundId} />
              </Box>
              {onAndrat && (
                <Tooltip title="Redigera kundprojektet">
                  <IconButton size="small" onClick={() => setRedigera(kp)} aria-label={`Redigera ${kp.namn}`}>
                    <EditIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {leadIds.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
                {leadIds.map(id => (
                  <Chip key={id} size="small" clickable label={leadMap.get(id)?.title || 'Lead'}
                    onClick={() => navigate(`/crm/leads/${id}`)}
                    sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#FFF3E0', color: ORANGE }} />
                ))}
              </Box>
            )}
          </Box>
        );
      })}
      {redigera && (
        <KundprojektRedigering kundprojekt={redigera} uid={uid} onClose={() => setRedigera(null)}
          onSparad={r => { onAndrat(r); visaKvitto(r.konflikt ? KONFLIKT_TEXT : 'Sparat', r.konflikt); }} />
      )}
      {kvitto}
    </Box>
  );
}

// ── Kontaktrutan ────────────────────────────────────────────────────────────

/** Kontaktens kundprojekt med rollIProjektet, som små chip. */
export function KontaktKundprojekt({ kps, kundId, kontaktId, navigate }) {
  const mina = kps.filter(kp => (kp.parter || [])
    .some(p => p.kundId === kundId && (p.kontakter || []).some(k => k.kontaktId === kontaktId)));
  if (!mina.length) return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
      {sorteraKp(mina).map(kp => {
        const roll = kontaktensRoll(kp, kundId, kontaktId);
        return (
          <Chip key={kp.id} size="small" icon={<ConstructionIcon />}
            label={roll ? `${kp.namn} · ${roll}` : kp.namn}
            clickable={!!navigate} onClick={navigate ? () => navigate(`/crm/kundprojekt/${kp.id}`) : undefined}
            sx={{ height: 20, fontSize: '0.62rem', bgcolor: arSakerhetsskyddat(kp) ? '#FFF3E0' : '#f3f3f3',
              '& .MuiChip-icon': { fontSize: 12, color: arSakerhetsskyddat(kp) ? ORANGE : '#757575' } }} />
        );
      })}
    </Box>
  );
}

// ── Leadsidan ───────────────────────────────────────────────────────────────

/**
 * Dialogen "Koppla kundprojekt": kundens befintliga först, sedan sökning
 * bland alla kundprojekt.
 */
function KopplaDialog({ open, onClose, leadId, kundId, kundensKps, kopplade, uid, onKopplad }) {
  const [sok, setSok] = useState('');
  const [alla, setAlla] = useState(null);
  const [sparar, setSparar] = useState('');
  const [fel, setFel] = useState('');
  const kopplat = new Set(kopplade);

  async function hamtaAlla() {
    if (alla) return;
    try { setAlla(await laddaAllaKundprojekt()); } catch (e) { setFel(e.message); setAlla([]); }
  }

  async function koppla(kp) {
    setSparar(kp.id);
    setFel('');
    try {
      onKopplad(await sparaKundprojektAndringar(kp, medLead(kp, leadId, kundId), uid));
      onClose();
    } catch (e) {
      setFel(e.message || String(e));
    } finally {
      setSparar('');
    }
  }

  const q = sok.trim().toLowerCase();
  const kundens = sorteraKp(kundensKps.filter(kp => !kopplat.has(kp.id)));
  const kundensIds = new Set(kundensKps.map(kp => kp.id));
  const ovriga = q && alla
    ? sorteraKp(alla.filter(kp => !kopplat.has(kp.id) && !kundensIds.has(kp.id)
      && [kp.namn, kp.ort, kp.beskrivning].some(x => String(x || '').toLowerCase().includes(q)))).slice(0, 30)
    : [];
  const rad = kp => (
    <ListItemButton key={kp.id} onClick={() => koppla(kp)} disabled={!!sparar}>
      <ListItemText primary={<Rubrik kp={kp} />}
        secondary={[etikett(KP_BYGGSKEDEN, kp.byggskede), kp.ort].filter(Boolean).join(' · ')} />
      {sparar === kp.id ? <CircularProgress size={16} /> : <LinkIcon sx={{ fontSize: 18, color: '#FF6B00' }} />}
    </ListItemButton>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Koppla kundprojekt</DialogTitle>
      <DialogContent>
        <List dense subheader={<ListSubheader disableGutters>Kundens kundprojekt</ListSubheader>}>
          {kundens.length ? kundens.map(rad)
            : <Typography variant="caption" color="text.secondary">Inga fler kundprojekt hos kunden.</Typography>}
        </List>
        <TextField size="small" fullWidth sx={{ mt: 1 }} label="Sök bland alla kundprojekt"
          value={sok} onFocus={hamtaAlla} onChange={e => { setSok(e.target.value); hamtaAlla(); }} />
        {q && (
          <List dense>
            {alla == null ? <CircularProgress size={18} sx={{ m: 1 }} />
              : ovriga.length ? ovriga.map(rad)
                : <Typography variant="caption" color="text.secondary">Inga träffar.</Typography>}
          </List>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Är kunden inte part i kundprojektet läggs den till, utan roll.
        </Typography>
        {fel && <Alert severity="error" sx={{ mt: 1 }}>{fel}</Alert>}
      </DialogContent>
      <DialogActions><Button onClick={onClose} sx={{ color: 'text.secondary' }}>Stäng</Button></DialogActions>
    </Dialog>
  );
}

/** Liten dropdown direkt på raden (byggskede, status). */
function Snabbval({ label, value, lista, onChange, disabled }) {
  return (
    <TextField select size="small" variant="standard" value={value || ''} disabled={disabled}
      onChange={e => onChange(e.target.value)} onClick={e => e.stopPropagation()}
      inputProps={{ 'aria-label': label }}
      sx={{ minWidth: 0, '& .MuiInputBase-root': { fontSize: '0.72rem' }, '& .MuiSelect-select': { py: 0.25 } }}>
      {lista.map(x => <MenuItem key={x.key} value={x.key} sx={{ fontSize: '0.8rem' }}>{x.label}</MenuItem>)}
    </TextField>
  );
}

/** "Testkörning 1 nov – 15 dec · ansvarig Anna" */
function TestkorningText({ t, kontakter }) {
  if (!t) return null;
  const ansvarig = kontakter.find(k => k.id === t.ansvarigKontaktId)?.name;
  const period = [t.start, t.slut].filter(Boolean).join(' – ');
  return (
    <Typography variant="caption" sx={{ display: 'block', color: '#00897B', fontWeight: 600 }}>
      Testkörning {period || 'utan datum'}{ansvarig ? ` · ansvarig ${ansvarig}` : ''}
    </Typography>
  );
}

/**
 * Leadets kundprojekt på leadsidan: snabbändring av byggskede och status på
 * raden, pennan öppnar redigeringen på plats (alla fält, parter och
 * testkörningen för det här leadet), "Ta bort från lead" tar bara bort
 * kopplingen. Kundprojektet raderas bara i kundprojektvyn.
 * kps: leadets kundprojekt. kundensKps: kundens (för kopplingsdialogen).
 * kontakter: kundens kontakter (namn på ansvarig för testkörning).
 * onAndrat(resultat) efter varje sparande.
 */
export function LeadKundprojekt({ leadId, kundId, kps, kundensKps, uid, onAndrat, laddar, navigate, kontakter = [], companies }) {
  const [koppla, setKoppla] = useState(false);
  const [redigera, setRedigera] = useState(null);
  const [upptagen, setUpptagen] = useState('');
  const [kvitto, visaKvitto] = useSparatKvitto();

  function sparat(r) {
    onAndrat(r);
    visaKvitto(r.konflikt ? KONFLIKT_TEXT : 'Sparat', r.konflikt);
  }

  async function andra(kp, andring) {
    setUpptagen(kp.id);
    try {
      sparat(await sparaKundprojektAndringar(kp, { ...kp, ...andring }, uid));
    } catch (e) {
      visaKvitto('Kunde inte spara: ' + (e.message || e), true);
    } finally {
      setUpptagen('');
    }
  }

  async function taBortFranLead(kp) {
    if (!window.confirm(`Ta bort kopplingen mellan leadet och kundprojektet "${kp.namn}"? Kundprojektet finns kvar.`)) return;
    setUpptagen(kp.id);
    try {
      sparat(await sparaKundprojektAndringar(kp, utanLead(kp, leadId), uid));
    } catch (e) {
      visaKvitto('Kunde inte ta bort kopplingen: ' + (e.message || e), true);
    } finally {
      setUpptagen('');
    }
  }

  return (
    <>
      {laddar ? <CircularProgress size={18} sx={{ mt: 1 }} /> : !kps.length ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Inga kundprojekt kopplade till leadet.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
          {sorteraKp(kps).map(kp => (
            <Box key={kp.id} sx={{ p: 1.25, borderRadius: 1.5, bgcolor: '#fafafa',
              borderLeft: arSakerhetsskyddat(kp) ? `3px solid ${ORANGE}` : '3px solid transparent',
              opacity: upptagen === kp.id ? 0.6 : 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Rubrik kp={kp} navigate={navigate} />
                  <Detaljer kp={kp} kundId={kundId} />
                  <TestkorningText t={kp.testkorningar?.[leadId]} kontakter={kontakter} />
                </Box>
                <Tooltip title="Redigera kundprojektet">
                  <IconButton size="small" onClick={() => setRedigera(kp)} aria-label={`Redigera ${kp.namn}`}>
                    <EditIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
                <Snabbval label={`Byggskede för ${kp.namn}`} lista={KP_BYGGSKEDEN} value={kp.byggskede}
                  disabled={upptagen === kp.id} onChange={x => andra(kp, { byggskede: x })} />
                <Snabbval label={`Status för ${kp.namn}`} lista={KP_STATUSAR} value={kp.status || 'aktivt'}
                  disabled={upptagen === kp.id} onChange={x => andra(kp, { status: x })} />
                <Button size="small" startIcon={<LinkOffIcon sx={{ fontSize: '14px !important' }} />}
                  onClick={() => taBortFranLead(kp)} disabled={upptagen === kp.id}
                  sx={{ ml: 'auto', color: 'text.secondary', textTransform: 'none', fontSize: '0.7rem', py: 0 }}>
                  Ta bort från lead
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      )}
      <Button size="small" startIcon={<LinkIcon />} onClick={() => setKoppla(true)}
        sx={{ mt: 1, color: '#FF6B00', textTransform: 'none', fontSize: '0.72rem' }}>
        Koppla kundprojekt
      </Button>
      {koppla && (
        <KopplaDialog open onClose={() => setKoppla(false)} leadId={leadId} kundId={kundId}
          kundensKps={kundensKps} kopplade={kps.map(k => k.id)} uid={uid} onKopplad={sparat} />
      )}
      {redigera && (
        <KundprojektRedigering kundprojekt={redigera} leadId={leadId} companies={companies} uid={uid}
          onClose={() => setRedigera(null)} onSparad={sparat} />
      )}
      {kvitto}
    </>
  );
}

// ── Kanbankortet ────────────────────────────────────────────────────────────

/** Etikett med första kundprojektet ("+N"), orange vid säkerhetsskydd, ikon vid isolerad miljö. */
export function KanbanKundprojekt({ kps }) {
  if (!kps.length) return null;
  const forsta = sorteraKp(kps)[0];
  const skydd = kps.some(arSakerhetsskyddat);
  const isolerad = kps.some(arIsoleradMiljo);
  return (
    <Tooltip title={`Kundprojekt: ${sorteraKp(kps).map(k => k.namn).join(', ')}`}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, mb: 0.5, maxWidth: '100%',
        px: 0.75, py: 0.15, borderRadius: 1, fontSize: '0.62rem',
        bgcolor: skydd ? '#FFF3E0' : '#f3f3f3', border: `1px solid ${skydd ? '#FFB74D' : '#e0e0e0'}`,
        color: skydd ? ORANGE : '#616161', fontWeight: skydd ? 700 : 500 }}>
        <ConstructionIcon sx={{ fontSize: 11 }} />
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{forsta.namn}</Box>
        {kps.length > 1 && <Box component="span" sx={{ flexShrink: 0 }}>+{kps.length - 1}</Box>}
        {isolerad && <CloudOffOutlinedIcon sx={{ fontSize: 11, color: '#6A1B9A', flexShrink: 0 }} />}
      </Box>
    </Tooltip>
  );
}

// ── Pipeline-sidan ──────────────────────────────────────────────────────────

/**
 * Rad under flödet: hur många kundprojekt som finns registrerade. Varje
 * siffra filtrerar tavlan och listan till leads med minst ett sådant
 * kundprojekt – samma filter som dropdownen "Kundprojekt". Valt filter
 * markeras med en ruta; klick igen tar bort det.
 */
export function KundprojektRad({ data, valt, onValj, laddar }) {
  const tal = [
    { key: 'aktiva',   n: data.aktiva,            text: 'aktiva',              tips: 'Visa leads med minst ett aktivt kundprojekt' },
    { key: 'har',      n: data.medLead,           text: 'med lead',            tips: 'Visa leads som har kundprojekt' },
    { key: 'skydd',    n: data.sakerhetsskyddade, text: 'säkerhetsskyddade',   tips: 'Visa leads med säkerhetsskyddsklassat kundprojekt', ikon: <ShieldOutlinedIcon sx={{ fontSize: 15, color: ORANGE }} /> },
    { key: 'isolerad', n: data.isolerad,          text: 'isolerad miljö',      tips: 'Visa leads med kundprojekt i isolerad/airgappad miljö', ikon: <CloudOffOutlinedIcon sx={{ fontSize: 15, color: '#6A1B9A' }} /> },
  ];
  return (
    <Paper sx={{ mb: 2.5, px: 2.5, py: 1.25, display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <ConstructionIcon sx={{ fontSize: 16, color: '#FF6B00' }} />
        <Typography variant="caption" fontWeight="bold"
          sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary' }}>
          Kundprojekt
        </Typography>
      </Box>
      {laddar ? <CircularProgress size={14} /> : (
        <>
          {tal.map(t => (
            <Tooltip key={t.key} title={valt === t.key ? 'Visa hela tavlan igen' : t.tips}>
              <Box component="button" type="button" onClick={() => onValj(valt === t.key ? null : t.key)}
                aria-pressed={valt === t.key}
                sx={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'baseline', gap: 0.6,
                  px: 1, py: 0.25, borderRadius: 1,
                  bgcolor: valt === t.key ? '#FFF3E0' : 'transparent',
                  outline: valt === t.key ? '2px solid #FF6B00' : 'none',
                  '& .kp-text': { textDecoration: valt === t.key ? 'underline' : 'none', fontWeight: valt === t.key ? 700 : 400 },
                  '&:hover': { bgcolor: '#FFF8F2' }, '&:focus-visible': { outline: '2px solid #FF6B00' } }}>
                {t.ikon && <Box sx={{ alignSelf: 'center', display: 'flex' }}>{t.ikon}</Box>}
                <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1 }}>{t.n}</Typography>
                <Typography className="kp-text" variant="body2" color="text.secondary">{t.text}</Typography>
              </Box>
            </Tooltip>
          ))}
          <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
            {data.nyaDennaManad} nya denna månad · {data.totalt} totalt
          </Typography>
        </>
      )}
    </Paper>
  );
}
